import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * `useLinkedScrollLeft(refs)` — keep the `scrollLeft` of every element in
 * `refs` equal, in EVERY direction. Added 2026-09-26 for the order tables'
 * one-page-scroll layout (owner: *"the orders are in a scroll window on a scroll
 * page! a scroll within a scroll? not nice UI desing"*, then, offered options,
 * *"One page scroll, pinned header"*).
 *
 * `OrderTableShell` splits its table into THREE horizontal panes: the pinned
 * header, the rows, and a sticky-bottom scrollbar (the 2026-08-10 ruling *"THe
 * horizontal scroll bars iin table views on al pages should be sticky"* still
 * binds, and the rows' own bar is at the bottom of a 100-row table). Each of the
 * three can be moved by the user: the rows by a swipe, a trackpad or focus; the
 * bar by a drag; the header by a swipe or by Tab reaching an off-screen sort
 * button, which scrolls even a clipping pane to reveal the focus.
 *
 * 🔑 WHY NOT `useFollowScrollLeft` (TWICE). That hook is ONE-WAY on purpose and
 * its docblock says why: a two-way sync needs a re-entrancy latch or it
 * PING-PONGS. The browser fires a scroll event for a PROGRAMMATIC write one
 * frame later, so while the user drags the bar to 120 the rows' echo of the
 * previous frame's 110 arrives and, unlatched, drags the bar back to 110.
 *
 * 🔑 THE LATCH IS PER-TARGET ECHO SUPPRESSION: every write records the value it
 * wrote on that element; that element's next scroll event carrying the same
 * value (±1px — scrollLeft is fractional on a zoomed display) is the echo and is
 * dropped. Anything else is a real scroll and is propagated. Order of delivery
 * does not matter, which is the property a "busy" flag does not have.
 *
 * 🔑 THE TRUTH on re-render is the LAST value a user scroll propagated, and
 * before any scroll the FIRST mounted pane: a layout effect with no deps aligns
 * every other pane to it after every render, because a render can mount a pane
 * (the bar appears only while the table overflows; a Breakdown group expands)
 * with no scroll event. 🔀 Until the Breakdown joined (2026-09-26) it was "the
 * first ref", which is only safe while the first pane can never unmount — on
 * the Breakdown expanding an EARLIER group would have snapped every later
 * group's panes back to 0. For the order shell the two rules agree (its rows
 * pane is first and always mounted, and it carries every propagated value).
 *
 * 🔀 `panes` is either an array of refs (the order shell's fixed three) or a
 * FUNCTION returning the live elements (the Breakdown's N groups × 2 panes +
 * the bar, found by a DOM query at effect time — refs built during render would
 * miss the panes that render mounts). The function form is called only inside
 * effects, after commit.
 *
 * Elements are tracked in STATE for `useDragScroll`'s reason — a `ref.current`
 * is not reactive, so a listener keyed on the ref never sees a late mount.
 * 🪤 The change test compares LENGTH as well as each slot: a list that SHRINKS
 * (a group collapses) passes an element-wise `some` over the SHORTER list, so
 * without the length check the removed pane kept its listener and kept being
 * written. Pinned by 1.9 in `useLinkedScrollLeft.test.js`.
 *
 * jsdom retains an assigned `scrollLeft` and fires no scroll event for it: the
 * tests dispatch the events by hand, so they prove the latch LOGIC, never that
 * a browser delivers events in the order simulated. That is a browser check.
 */
const ECHO_TOLERANCE_PX = 1;

const liveElements = (panes) => (typeof panes === 'function' ? panes() : panes.map((r) => r.current));

export default function useLinkedScrollLeft(panes) {
  const [els, setEls] = useState([]);
  const lastLeft = useRef(null);
  useEffect(() => {
    const next = liveElements(panes);
    // Keep the SAME array unless an element changed, so the listener effect
    // below re-subscribes only on a real mount/unmount.
    setEls((cur) => (next.length !== cur.length || next.some((el, i) => el !== cur[i]) ? next : cur));
  });

  useLayoutEffect(() => {
    const live = liveElements(panes).filter(Boolean);
    if (!live.length) return;
    const x = lastLeft.current == null ? live[0].scrollLeft : lastLeft.current;
    live.forEach((el) => {
      if (Math.abs(el.scrollLeft - x) >= ECHO_TOLERANCE_PX) el.scrollLeft = x;
    });
  });

  useEffect(() => {
    const live = els.filter(Boolean);
    // ONE pane is still listened to: its genuine scrolls must become the truth
    // a pane mounting later is aligned to (the Breakdown with one group open).
    if (!live.length) return undefined;
    const expectedEcho = new Map();
    const handlers = live.map((src) => {
      const onScroll = () => {
        const x = src.scrollLeft;
        if (expectedEcho.has(src)) {
          const echo = expectedEcho.get(src);
          expectedEcho.delete(src);
          if (Math.abs(x - echo) < ECHO_TOLERANCE_PX) return;
        }
        lastLeft.current = x;
        live.forEach((dst) => {
          if (dst === src || Math.abs(dst.scrollLeft - x) < ECHO_TOLERANCE_PX) return;
          const before = dst.scrollLeft;
          dst.scrollLeft = x;
          const after = dst.scrollLeft;
          // 🪤 RECORD AN ECHO ONLY FOR A WRITE THAT MOVED THE PANE (review fix,
          // 2026-09-26). A write CLAMPED to the value the pane already held (it
          // was at its max) changes nothing, so the browser fires NO event and
          // an entry recorded for it could never be consumed — the pane's next
          // GENUINE scroll to that value would then be swallowed as an echo.
          // So: moved (≥ tolerance) → record what it ACTUALLY holds (a clamped
          // write's echo carries the clamped value); otherwise → DROP any entry,
          // because no event is coming. A sub-tolerance fractional move that
          // does fire an event is then treated as genuine, and propagating it is
          // harmless: every other pane is already within tolerance of it, so the
          // `< ECHO_TOLERANCE_PX` guard above writes nothing and nothing loops.
          // Pinned by 1.7 (clamped no-op write) and 1.8 (the entry is CLEARED
          // once consumed) in `useLinkedScrollLeft.test.js`.
          if (Math.abs(after - before) >= ECHO_TOLERANCE_PX) expectedEcho.set(dst, after);
          else expectedEcho.delete(dst);
        });
      };
      src.addEventListener('scroll', onScroll, { passive: true });
      return [src, onScroll];
    });
    return () => handlers.forEach(([el, fn]) => el.removeEventListener('scroll', fn));
  }, [els]);
}
